import React, { useMemo, useState } from "react";

import { fetchJsonWithAuth } from "../api/client";
import { useAdminAuth } from "../auth/AdminAuthProvider";
import { AdminProtectedRoute } from "../auth/AdminProtectedRoute";
import { AdminShell } from "../components/AdminShell";
import { LoadingLine, Modal, NoticeBanner, Pager } from "../components/ui";
import { useAuditFilters } from "../hooks/useAuditFilters";
import { buildCacheKey, getFreshCache, setCache } from "../shared/adminCache";
import { buildJsonDiffSummary, buildJsonPathDiff, downloadCsvFile, safeParseJson } from "../shared/helpers";
import { AuditModalPayload, AuditRow, ChangedColumnRow } from "../shared/siteContent";

type Notice = { type: "ok" | "warn" | "error"; message: string } | null;

type HistoryResponse = {
  success: boolean;
  rows: AuditRow[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

type HistoryCacheData = {
  rows: AuditRow[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

const ADMIN_CACHE_TTL_MS = 2 * 60 * 1000;

// history API 응답을 화면 소비용 기본 shape로 정규화한다.
function normalizeHistoryResponse(response: Partial<HistoryResponse> | null | undefined) {
  if (!response || typeof response !== "object") {
    return {
      rows: [] as AuditRow[],
      page: 1,
      pageSize: 20,
      totalPages: 1,
      total: 0,
      usedFallback: true,
    };
  }

  return {
    rows: Array.isArray(response.rows) ? response.rows : [],
    page: response.page || 1,
    pageSize: response.pageSize || 20,
    totalPages: response.totalPages || 1,
    total: response.total || 0,
    usedFallback: false,
  };
}

// diff 값을 모달/테이블에 표시할 문자열 형태로 직렬화한다.
function stringifyDiffValue(value: unknown) {
  if (typeof value === "undefined") return "(없음)";
  if (value === null) return "null";
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

// 긴 diff 값을 한 줄 미리보기로 축약한다.
function toInlinePreview(value: unknown, max = 140) {
  const raw = stringifyDiffValue(value).replace(/\s+/g, " ").trim();
  if (raw.length <= max) return raw;
  return `${raw.slice(0, max)}...`;
}

// diff 값이 길거나 개행을 포함해 상세 펼침이 필요한지 판단한다.
function shouldShowExpanded(value: unknown, max = 140) {
  const raw = stringifyDiffValue(value);
  return raw.length > max || raw.includes("\n");
}

// JSONPath("$....")를 컬럼 경로 표기용 문자열로 정규화한다.
function normalizeColumnPath(path: string) {
  const text = String(path || "").trim();
  if (!text || text === "$") return "(root)";
  return text.replace(/^\$\./, "").replace(/^\$/, "");
}

// 구조 diff 항목을 table-column-after JSON 스키마로 변환한다.
function buildChangedColumns(targetKey: string, entries: Array<{ path: string; afterValue?: unknown }>): ChangedColumnRow[] {
  const table = `site_config.${String(targetKey || "unknown").trim() || "unknown"}`;
  return entries.map((entry) => {
    const next: ChangedColumnRow = {
      table,
      column: normalizeColumnPath(entry.path),
    };
    if (typeof entry.afterValue !== "undefined") {
      next.after = entry.afterValue;
    }
    return next;
  });
}

/**
 * 콘텐츠 변경 이력 페이지.
 * audit_log 목록 필터링과 before/after JSON 비교 모달을 제공한다.
 */
export function HistoryPage() {
  const { getIdToken, ready, user } = useAdminAuth();
  const { filters, updateFilter, buildQuery } = useAuditFilters();
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [notice, setNotice] = useState<Notice>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [modalPayload, setModalPayload] = useState<AuditModalPayload | null>(null);

  // 필터 조건 기준으로 history를 로드하고 캐시/동기화 상태를 관리한다.
  async function load(options?: { forceNetwork?: boolean }) {
    const forceNetwork = Boolean(options?.forceNetwork);
    const query = buildQuery();
    const queryKey = query.toString() || "default";
    const cacheKey = user ? buildCacheKey(user.uid, "history", queryKey) : "";
    let hasWarmData = false;

    setNotice(null);
    if (!forceNetwork && cacheKey) {
      const cached = getFreshCache<HistoryCacheData>(cacheKey, ADMIN_CACHE_TTL_MS);
      if (cached?.data) {
        setRows(cached.data.rows);
        setTotal(cached.data.total);
        setTotalPages(cached.data.totalPages || 1);
        hasWarmData = true;
      }
    }
    setLoading(!hasWarmData);
    setSyncing(hasWarmData);
    if (hasWarmData) {
      setNotice({ type: "warn", message: "서버 최신 정보를 동기화 중입니다." });
    }

    try {
      const history = await fetchJsonWithAuth<HistoryResponse>(`/api/admin/history?${query.toString()}`, { method: "GET" }, getIdToken);
      const normalized = normalizeHistoryResponse(history);
      setRows(normalized.rows);
      setTotal(normalized.total);
      setTotalPages(normalized.totalPages || 1);
      updateFilter("page", normalized.page || 1);
      updateFilter("pageSize", normalized.pageSize || 20);
      if (cacheKey) {
        setCache<HistoryCacheData>(cacheKey, {
          rows: normalized.rows,
          total: normalized.total,
          totalPages: normalized.totalPages || 1,
          page: normalized.page || 1,
          pageSize: normalized.pageSize || 20,
        });
      }

      if (normalized.usedFallback) {
        setNotice({ type: "warn", message: "history 응답이 비어 있어 빈 목록으로 표시 중입니다." });
      } else {
        setNotice(null);
      }
    } catch (caught) {
      if (hasWarmData) {
        setNotice({ type: "warn", message: "캐시 데이터를 표시 중이며 최신 동기화에 실패했습니다." });
      } else {
        setNotice({ type: "error", message: caught instanceof Error ? caught.message : "히스토리 조회에 실패했습니다." });
      }
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  }

  React.useEffect(() => {
    if (!ready || !user) return;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, user?.uid, filters.page, filters.pageSize]);

  const diffModel = useMemo(() => {
    if (!modalPayload || modalPayload.mode !== "after_diff") return null;

    const beforeRaw = String(modalPayload.beforeJson || "");
    const afterRaw = String(modalPayload.afterJson || "");
    const beforeParsed = safeParseJson(beforeRaw);
    const afterParsed = safeParseJson(afterRaw);

    // 1순위: JSON 구조 diff(path 단위)로 변경 의도를 바로 보여준다.
    if (beforeParsed.ok && afterParsed.ok) {
      const entries = buildJsonPathDiff(beforeParsed.value, afterParsed.value);
      const summary = buildJsonDiffSummary(entries);
      return {
        kind: "structural" as const,
        entries,
        summary,
      };
    }

    // fallback: JSON 파싱이 안 되면 줄 단위 diff로 안전하게 폴백한다.
    const beforeLines = beforeRaw.split("\n");
    const afterLines = afterRaw.split("\n");
    const maxLength = Math.max(beforeLines.length, afterLines.length);
    const lines = Array.from({ length: maxLength }).map((_, index) => ({
      before: beforeLines[index] || "",
      after: afterLines[index] || "",
      changed: beforeLines[index] !== afterLines[index],
    }));
    const parseMessage = `JSON 파싱 실패로 줄 단위 비교를 표시합니다. before: ${beforeParsed.error || "-"}, after: ${afterParsed.error || "-"}`;

    return {
      kind: "line" as const,
      lines,
      parseMessage,
    };
  }, [modalPayload]);

  const changedColumns = useMemo(() => {
    if (!modalPayload || modalPayload.mode !== "after_diff") return [];
    if (!diffModel || diffModel.kind !== "structural") return [];
    return buildChangedColumns(modalPayload.targetKey, diffModel.entries);
  }, [diffModel, modalPayload]);

  // 현재 필터 결과를 CSV 파일로 다운로드한다.
  function exportCsv() {
    // 현재 필터/페이지 상태로 조회된 rows를 그대로 내보낸다.
    const headers = ["No", "관리자", "target_key", "before_json", "after_json", "created_at", "ip"];
    const csvRows = rows.map((row, index) => [
      (filters.page - 1) * filters.pageSize + index + 1,
      row.admin_display,
      row.target_key,
      row.before_json,
      row.after_json,
      row.created_at,
      row.ip || "",
    ]);
    downloadCsvFile(`site_content_audit_${Date.now()}.csv`, headers, csvRows);
    setNotice({ type: "ok", message: "CSV 다운로드를 시작했습니다." });
  }

  return (
    <AdminProtectedRoute>
      <AdminShell title="홈페이지 콘텐츠 업데이트 내역" description="audit_log 기반 조회 화면입니다. before/after JSON을 상세 비교할 수 있습니다.">
        <NoticeBanner notice={notice} />

        <div className="cm-filter-grid">
          <div className="cm-filter-item cm-filter-item-grow">
            <label className="cm-filter-label" htmlFor="cm-history-filter-q">
              검색
            </label>
            <input
              className="cm-input"
              id="cm-history-filter-q"
              onChange={(event) => updateFilter("q", event.target.value)}
              placeholder="관리자/target/ip 검색"
              value={filters.q}
            />
          </div>
          <div className="cm-filter-item">
            <label className="cm-filter-label" htmlFor="cm-history-filter-target">
              대상
            </label>
            <select className="cm-select" id="cm-history-filter-target" onChange={(event) => updateFilter("target_key", event.target.value)} value={filters.target_key}>
              <option value="all">all</option>
              <option value="popup">popup</option>
              <option value="ir_ko">ir_ko</option>
              <option value="ir_en">ir_en</option>
              <option value="history">history</option>
              <option value="youtube">youtube</option>
            </select>
          </div>
          <div className="cm-filter-item">
            <label className="cm-filter-label" htmlFor="cm-history-filter-sort">
              정렬
            </label>
            <select className="cm-select" id="cm-history-filter-sort" onChange={(event) => updateFilter("sort", event.target.value as "latest" | "oldest")} value={filters.sort}>
              <option value="latest">최신순</option>
              <option value="oldest">오래된순</option>
            </select>
          </div>
          <div className="cm-filter-item">
            <label className="cm-filter-label" htmlFor="cm-history-filter-start">
              시작일
            </label>
            <input className="cm-input" id="cm-history-filter-start" onChange={(event) => updateFilter("start", event.target.value)} type="date" value={filters.start} />
          </div>
          <div className="cm-filter-item">
            <label className="cm-filter-label" htmlFor="cm-history-filter-end">
              종료일
            </label>
            <input className="cm-input" id="cm-history-filter-end" onChange={(event) => updateFilter("end", event.target.value)} type="date" value={filters.end} />
          </div>
          <div className="cm-filter-item">
            <label className="cm-filter-label" htmlFor="cm-history-filter-size">
              개수
            </label>
            <select className="cm-select" id="cm-history-filter-size" onChange={(event) => updateFilter("pageSize", Number(event.target.value))} value={filters.pageSize}>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          <div className="cm-filter-item cm-filter-item-full">
            <span className="cm-filter-label">동작</span>
            <div className="cm-actions">
              <button className="cm-btn cm-btn-apply" disabled={loading} onClick={() => void load()} type="button">
                적용
              </button>
              <button className="cm-btn cm-btn-secondary" onClick={exportCsv} type="button">
                CSV 다운로드
              </button>
              <button className="cm-btn cm-btn-refresh" disabled={loading} onClick={() => void load({ forceNetwork: true })} type="button">
                새로고침
              </button>
            </div>
          </div>
        </div>

        {loading ? <LoadingLine message="audit_log를 불러오는 중입니다..." /> : null}
        {!loading && syncing ? <LoadingLine message="서버 최신 정보를 동기화 중입니다..." /> : null}

        <div className="cm-table-wrap">
          <table className="cm-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>관리자</th>
                <th>target_key</th>
                <th>before_json</th>
                <th>after_json</th>
                <th>created_at</th>
                <th>ip</th>
              </tr>
            </thead>
            <tbody>
              {!rows.length && !loading ? (
                <tr>
                  <td colSpan={7}>조건에 맞는 변경 이력이 없습니다.</td>
                </tr>
              ) : null}
              {rows.map((row, index) => (
                <tr key={row.id}>
                  <td data-label="No.">{total - ((filters.page - 1) * filters.pageSize + index)}</td>
                  <td data-label="관리자">{row.admin_display}</td>
                  <td data-label="target_key">{row.target_key}</td>
                  <td data-label="before_json">
                    <button
                      className="cm-btn cm-btn-preview"
                      onClick={() =>
                        setModalPayload({
                          mode: "before",
                          title: "변경 전 JSON",
                          beforeJson: row.before_json,
                          targetKey: row.target_key,
                          createdAt: row.created_at,
                        })
                      }
                      type="button"
                    >
                      변경 전 보기
                    </button>
                  </td>
                  <td data-label="after_json">
                    <button
                      className="cm-btn cm-btn-preview"
                      onClick={() =>
                        setModalPayload({
                          mode: "after_diff",
                          title: "변경 후 JSON (JSON path diff)",
                          beforeJson: row.before_json,
                          afterJson: row.after_json,
                          targetKey: row.target_key,
                          createdAt: row.created_at,
                        })
                      }
                      type="button"
                    >
                      변경 차이 보기
                    </button>
                  </td>
                  <td data-label="created_at">{row.created_at}</td>
                  <td data-label="ip">{row.ip || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pager
          page={filters.page}
          totalPages={totalPages}
          onPageChange={(page) => {
            updateFilter("page", page);
          }}
        />

        <Modal onClose={() => setModalPayload(null)} open={!!modalPayload} title={modalPayload?.title || "JSON"}>
          {modalPayload ? (
            <div className="cm-panel-grid">
              <div className="cm-muted">
                {modalPayload.targetKey} · {modalPayload.createdAt}
              </div>
              {modalPayload.mode === "before" ? (
                <pre className="cm-json-soft">{modalPayload.beforeJson || "(empty)"}</pre>
              ) : (
                <div className="cm-panel-grid">
                  <div className="cm-info">비교 방식 안내: JSON을 파싱해 path 단위(추가/삭제/수정)로 비교합니다.</div>

                  {diffModel?.kind === "structural" ? (
                    <>
                      <div className="cm-diff-summary-grid">
                        <div className="cm-kv-card">
                          <span className="cm-muted">총 변경</span>
                          <strong>{diffModel.summary.total}</strong>
                        </div>
                        <div className="cm-kv-card">
                          <span className="cm-muted">추가</span>
                          <strong style={{ color: "#166534" }}>{diffModel.summary.added}</strong>
                        </div>
                        <div className="cm-kv-card">
                          <span className="cm-muted">삭제</span>
                          <strong style={{ color: "#475569" }}>{diffModel.summary.removed}</strong>
                        </div>
                        <div className="cm-kv-card">
                          <span className="cm-muted">수정</span>
                          <strong style={{ color: "#991b1b" }}>{diffModel.summary.changed}</strong>
                        </div>
                      </div>

                      <section className="cm-list-card">
                        <h4>변경 후 컬럼 요약 (table-column-after)</h4>
                        <pre className="cm-json-soft cm-json-fixed-y">
                          {JSON.stringify(changedColumns, null, 2)}
                        </pre>
                      </section>

                      {diffModel.entries.length ? (
                        <div className={`cm-table-wrap ${diffModel.entries.length > 5 ? "cm-table-wrap-scroll-y" : ""}`.trim()}>
                          <table className="cm-table">
                            <thead>
                              <tr>
                                <th>구분</th>
                                <th>path</th>
                                <th>before</th>
                                <th>after</th>
                              </tr>
                            </thead>
                            <tbody>
                              {diffModel.entries.map((entry, index) => (
                                <tr className={`cm-diff-row cm-diff-row-${entry.type}`.trim()} key={`${entry.path}-${entry.type}-${index}`}>
                                  <td data-label="구분">
                                    <span className={`cm-diff-tag cm-diff-tag-${entry.type}`.trim()}>
                                      {entry.type === "added" ? "추가" : entry.type === "removed" ? "삭제" : "수정"}
                                    </span>
                                  </td>
                                  <td data-label="path">
                                    <code>{entry.path}</code>
                                  </td>
                                  <td data-label="before">
                                    <div className="cm-diff-value">
                                      <code className="cm-diff-inline">{toInlinePreview(entry.beforeValue)}</code>
                                      {shouldShowExpanded(entry.beforeValue) ? (
                                        <details className="cm-diff-details">
                                          <summary>전체 보기</summary>
                                          <pre className="cm-json-soft">{stringifyDiffValue(entry.beforeValue)}</pre>
                                        </details>
                                      ) : null}
                                    </div>
                                  </td>
                                  <td data-label="after">
                                    <div className="cm-diff-value">
                                      <code className="cm-diff-inline">{toInlinePreview(entry.afterValue)}</code>
                                      {shouldShowExpanded(entry.afterValue) ? (
                                        <details className="cm-diff-details">
                                          <summary>전체 보기</summary>
                                          <pre className="cm-json-soft">{stringifyDiffValue(entry.afterValue)}</pre>
                                        </details>
                                      ) : null}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="cm-notice">before/after 구조 차이가 없습니다.</div>
                      )}
                    </>
                  ) : null}

                  {diffModel?.kind === "line" ? (
                    <>
                      <div className="cm-notice warn">{diffModel.parseMessage}</div>
                      <div className={`cm-table-wrap ${diffModel.lines.length > 5 ? "cm-table-wrap-scroll-y" : ""}`.trim()}>
                        <table className="cm-table">
                          <thead>
                            <tr>
                              <th>Before</th>
                              <th>After</th>
                            </tr>
                          </thead>
                          <tbody>
                            {diffModel.lines.map((line, index) => (
                              <tr key={`${index}-${line.before}-${line.after}`}>
                                <td data-label="Before">
                                  <pre className="cm-json-soft">{line.before || " "}</pre>
                                </td>
                                <td data-label="After">
                                  <pre className={`cm-json-soft ${line.changed ? "cm-json-soft-diff-after" : "cm-json-soft-after"}`.trim()}>{line.after || " "}</pre>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : null}
                </div>
              )}
            </div>
          ) : null}
        </Modal>
      </AdminShell>
    </AdminProtectedRoute>
  );
}
